/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import moment, { Moment } from "moment";
import { useCallback, useEffect, useState } from "react";
import { DatePickerField } from "../forms/fields/DatePickerField";
import { SelectField } from "../forms/fields/SelectField";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { StyledText } from "../../assets/typography/typography";
import { colors } from "../colors";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import { breakpoints } from "../breakpoints";

type Props = {
  setFromDate: (date: Moment) => void;
  setToDate: (date: Moment) => void;
  fromDate: Moment;
  toDate: Moment;
  minDate?: Date;
};

const wrapperStyle = css`
  display: flex;
  flex-direction: row;
  gap: 8px;
  align-items: center;
  ${breakpoints.mobile} {
    flex-direction: column;
    width: fit-content;
    align-items: start;
  }
  ${breakpoints.tablet} {
    flex-direction: column;
    width: fit-content;
    align-items: start;
  }
`;

const arrowDownStyle = css`
  display: none;
  ${breakpoints.mobile} {
    display: flex;
    align-self: center;
  }
  ${breakpoints.tablet} {
    display: flex;
    align-self: center;
  }
`;

const arrowRightStyle = css`
  display: flex;
  ${breakpoints.mobile} {
    display: none;
  }
  ${breakpoints.tablet} {
    display: none;
  }
`;

enum FilterValueType {
  today = "Today",
  yesterday = "Yesterday",
  lastSevenDays = "Last 7 days",
  lastThirtydays = "Last 30 days",
  lastThreemonths = "Last 3 months",
  lastSixmonths = "Last 6 months",
  lastTwelvemonths = "Last 12 months",
  custom = "Custom",
}

export const DateFilter = ({
  setFromDate,
  setToDate,
  toDate,
  fromDate,
  minDate,
}: Props) => {
  // Not using these values in onChange as a user can have browser open over night where dates have changed
  const yesterday = moment().subtract(1, "day");
  const oneWeekAgo = moment().subtract(6, "day");
  const oneMonthAgo = moment().subtract(29, "day");
  const threeMonthsAgo = moment().subtract(3, "month");
  const sixMonthsAgo = moment().subtract(6, "month");
  const oneYearAgo = moment().subtract(1, "year");

  // Have to do this as Date object will force below useEffect to refire all the time if minDate is included in dependency array
  const formattedMinDate = minDate
    ? moment(minDate).startOf("day").format()
    : undefined;

  const returnOption = (value: FilterValueType, valueDate: Moment) => {
    return formattedMinDate
      ? valueDate.isSameOrAfter(moment(formattedMinDate))
        ? [value]
        : []
      : [value];
  };
  const callBackReturnOption = useCallback(returnOption, [formattedMinDate]);

  const options: FilterValueType[] = [
    FilterValueType.today,
    ...returnOption(FilterValueType.yesterday, yesterday),
    ...returnOption(FilterValueType.lastSevenDays, oneWeekAgo),
    ...returnOption(FilterValueType.lastThirtydays, oneMonthAgo),
    ...returnOption(FilterValueType.lastThreemonths, threeMonthsAgo),
    ...returnOption(FilterValueType.lastSixmonths, sixMonthsAgo),
    ...returnOption(FilterValueType.lastTwelvemonths, oneYearAgo),
    FilterValueType.custom,
  ];
  const [showCustomPicker, setShowCustomPicker] = useState(
    returnOption(FilterValueType.lastThirtydays, oneMonthAgo).length === 0
      ? true
      : false
  );
  const [filterValue, setFilterValue] = useState<FilterValueType>(
    returnOption(FilterValueType.lastThirtydays, oneMonthAgo).length > 0
      ? FilterValueType.lastThirtydays
      : FilterValueType.custom
  );

  useEffect(() => {
    const oneMonthAgo = moment().subtract(29, "day");
    const lastThirtyDaysIfAfterMinDate = callBackReturnOption(
      FilterValueType.lastThirtydays,
      oneMonthAgo
    );
    if (lastThirtyDaysIfAfterMinDate.length === 0) {
      setFromDate(moment(formattedMinDate).startOf("day"));
    } else {
      setFromDate(oneMonthAgo.startOf("day"));
    }
    setToDate(moment().endOf("day"));
  }, [setToDate, setFromDate, formattedMinDate, callBackReturnOption]);

  const onChange = (value: string | number) => {
    if (value === "Custom") {
      setShowCustomPicker(true);
      setFilterValue(FilterValueType.custom);
      return;
    }
    const startOfMinDate = moment(minDate).startOf("day");
    const today = moment().endOf("day");
    setShowCustomPicker(false);
    if (value === FilterValueType.lastThirtydays) {
      const oneMonthago = moment().subtract(29, "day").startOf("day");
      setToDate(today);
      if (minDate && oneMonthago.isBefore(minDate)) {
        setFromDate(startOfMinDate);
      } else {
        setFromDate(oneMonthago);
      }
      setFilterValue(FilterValueType.lastThirtydays);
      return;
    }

    if (value === FilterValueType.lastSevenDays) {
      const oneWeekago = moment().subtract(6, "day").startOf("day");
      setToDate(today);
      if (minDate && oneWeekago.isBefore(minDate)) {
        setFromDate(startOfMinDate);
      } else {
        setFromDate(oneWeekago);
      }
      setFilterValue(FilterValueType.lastSevenDays);
      return;
    }

    if (value === FilterValueType.lastThreemonths) {
      const threeMonthsAgo = moment().subtract(3, "month").startOf("day");
      setToDate(today);
      if (minDate && threeMonthsAgo.isBefore(minDate)) {
        setFromDate(startOfMinDate);
      } else {
        setFromDate(threeMonthsAgo);
      }
      setFilterValue(FilterValueType.lastThreemonths);
      return;
    }

    if (value === FilterValueType.lastSixmonths) {
      const sixMonthsAgo = moment().subtract(6, "month").startOf("day");
      setToDate(today);
      if (minDate && sixMonthsAgo.isBefore(minDate)) {
        setFromDate(startOfMinDate);
      } else {
        setFromDate(sixMonthsAgo);
      }
      setFilterValue(FilterValueType.lastSixmonths);
      return;
    }

    if (value === FilterValueType.today) {
      setToDate(today);
      if (minDate && today.isBefore(minDate)) {
        setFromDate(startOfMinDate.startOf("day"));
      } else {
        setFromDate(today);
      }
      setFilterValue(FilterValueType.today);
      return;
    }

    if (value === FilterValueType.yesterday) {
      const yesterday = moment().subtract(1, "day").startOf("day");
      setToDate(yesterday.clone().endOf("day"));
      if (minDate && yesterday.isBefore(minDate)) {
        setFromDate(startOfMinDate.startOf("day"));
      } else {
        setFromDate(yesterday.clone().startOf("day"));
      }
      setFilterValue(FilterValueType.yesterday);
      return;
    }

    if (value === FilterValueType.lastTwelvemonths) {
      const oneYearAgo = moment().subtract(1, "year").startOf("day");
      setToDate(today);
      if (minDate && oneYearAgo.isBefore(minDate)) {
        setFromDate(startOfMinDate);
      } else {
        setFromDate(oneYearAgo);
      }
      setFilterValue(FilterValueType.lastTwelvemonths);
    }
  };

  const selectOptions = options.map((y) => ({
    value: y,
    label: y,
  }));

  return (
    <div css={wrapperStyle}>
      {showCustomPicker && (
        <>
          <DatePickerField
            onChange={(date) => {
              setFromDate(moment(date).startOf("day"));
            }}
            width="fit-content"
            value={fromDate.format()}
            placeholder="From date"
            minDate={minDate}
            maxDate={toDate ? moment(toDate).endOf("day").toDate() : undefined}
          />
          <StyledText
            css={arrowRightStyle}
            className="bodyTextSemibold mb-0"
            color={colors.textPrimary}
          >
            <FontAwesomeIcon icon={solid("arrow-right")} />
          </StyledText>
          <StyledText
            css={arrowDownStyle}
            className="bodyTextSemibold mb-0"
            color={colors.textPrimary}
          >
            <FontAwesomeIcon icon={solid("arrow-down")} />
          </StyledText>
          <DatePickerField
            onChange={(date) => {
              setToDate(moment(date).endOf("day"));
            }}
            width="fit-content"
            minDate={fromDate ? moment(fromDate).toDate() : undefined}
            maxDate={moment().toDate()}
            value={toDate.format()}
            placeholder="To date"
          />
        </>
      )}
      <SelectField
        options={selectOptions}
        value={selectOptions?.find((option) => option.value === filterValue)}
        width="200px"
        onChange={(value) => {
          const newValue = value as { value: string; label: string };
          if (onChange) {
            onChange(newValue.value);
          }
        }}
      />
    </div>
  );
};
